/** Avisos do período de teste, da confirmação de e-mail e da liberação provisória de compliance; a tela de bloqueio ao fim do teste. */
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, dateTime, int } from '../api.ts';
import { useMe } from './AuthGate.tsx';

interface Waiver { id: string; scope: string; endsAt: string; acceptedAt: string | null; acceptedBy: string | null; canAccept: boolean }

export function AccountNotices() {
  const me = useMe();
  const resend = useMutation({ mutationFn: () => api('/auth/resend-verification', { method: 'POST' }) });
  const t = me.trial;
  const waiver = useQuery({ queryKey: ['compliance-waiver'], queryFn: () => api<Waiver | null>('/account/compliance-waiver'), staleTime: 300_000 });
  const qc = useQueryClient();
  const accept = useMutation({ mutationFn: () => api('/account/compliance-waiver/accept', { method: 'POST', json: {} }), onSuccess: () => qc.invalidateQueries({ queryKey: ['compliance-waiver'] }) });
  const w = waiver.data;
  if (!t && me.user.emailVerified && !w) return null;
  return (
    <div className="stack-y" style={{ gap: 8, marginBottom: 16 }}>
      {w && (
        <div className="notice small warn" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            <b>Liberação provisória de compliance</b> até {dateTime(w.endsAt)}: a conta opera enquanto os documentos são regularizados.
            A liberação não vale para antimicrobianos nem para medicamentos da Portaria 344.
          </span>
          <span style={{ flex: 1 }} />
          {w.acceptedAt ? <span className="muted">Termos aceitos em {dateTime(w.acceptedAt)}{w.acceptedBy ? ` por ${w.acceptedBy}` : ''}</span>
            : w.canAccept ? <button className="btn sm" type="button" disabled={accept.isPending} onClick={() => accept.mutate()}>Li e aceito os termos</button>
            : <span className="muted">Aguardando o aceite do dono ou de um usuário do grupo Admin da conta</span>}
        </div>
      )}
      {t && !t.expired && (
        <div className={`notice small${t.daysLeft <= 5 ? ' warn' : ''}`} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            <b>Período de teste:</b> {t.daysLeft === 1 ? 'último dia' : `${int(t.daysLeft)} dias restantes`} (até {dateTime(t.endsAt)}) ·{' '}
            {int(t.usage.products)} de {int(t.limits.products ?? 0)} produtos · até {int(t.limits.listingsPerChannel ?? 0)} anúncios por marketplace
          </span>
          <span style={{ flex: 1 }} />
          <Link className="btn sm" to="/empresa">Ver plano</Link>
        </div>
      )}
      {!me.user.emailVerified && (
        <div className="notice small warn" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>Confirme seu e-mail (<b>{me.user.email}</b>) para conectar os marketplaces. O link foi enviado no cadastro.</span>
          <span style={{ flex: 1 }} />
          {resend.isSuccess ? <span className="muted">Enviado. Confira também o spam.</span> : (
            <button className="btn sm" type="button" disabled={resend.isPending} onClick={() => resend.mutate()}>Reenviar e-mail</button>
          )}
        </div>
      )}
    </div>
  );
}

export function TrialEnded() {
  const me = useMe();
  const qc = useQueryClient();
  const logout = async () => {
    await api('/auth/logout', { method: 'POST' });
    qc.clear();
    location.href = '/login';
  };
  return (
    <div className="panel" style={{ maxWidth: 640, margin: '8vh auto', display: 'grid', gap: 14 }}>
      <h1 style={{ margin: 0 }}>O período de teste terminou</h1>
      <p style={{ margin: 0 }}>
        Os 30 dias de teste de <b>{me.tenant.name}</b> acabaram em {dateTime(me.trial?.endsAt)}. Seus produtos, configurações e
        conexões continuam guardados: assim que o plano for contratado, tudo volta a funcionar de onde parou.
      </p>
      <p style={{ margin: 0 }}>Fale com a equipe da Berytus para contratar: <a href="mailto:contato@berytus.com.br">contato@berytus.com.br</a>.</p>
      <div style={{ display: 'flex', gap: 8 }}>
        <Link className="btn" to="/empresa">Dados da empresa</Link>
        <button className="btn quiet" type="button" onClick={logout}>Sair</button>
      </div>
    </div>
  );
}
